import { create } from 'zustand';
import {
  getLeaveTypes,
  getLeaveBalance,
  getPendingLeaves,
  getApprovedLeaves,
  getDeclinedLeaves,
  createLeaveRequest,
  withdrawLeave as withdrawLeaveService,
  updateLeaveRequest as updateLeaveRequestService,
  deletePendingLeave as deletePendingLeaveService,
} from '@/services/EmployeeService/EmployeesLeavesService';
import { registerStoreReset } from '@/store/storeResetUtils';

// Type definitions
export interface LeaveType {
  id: number;
  leave_name: string;
}

export interface LeaveBalance {
  id: number;
  leave_type_id: number;
  leave_name: string;
  total_leaves: number;
  leaves_taken: number;
  remaining_leaves: number;
}

export interface LeaveRequest {
  leave_id: number;
  emp_id: string;
  leave_type_id: number;
  start_date: string;
  end_date: string;
  title: string;
  body: string;
  attachment?: string | null;
  status: 'pending' | 'approved' | 'declined' | 'withdrawn';
  approval_time?: string | null;
  managed_by?: string | null;
  managed_by_name?: string | null;
  created_at: string;
  leave_type?: string;
}

interface EmployeeLeaveStore {
  // State
  leaveTypes: LeaveType[];
  leaveBalance: LeaveBalance[];
  pendingLeaves: LeaveRequest[];
  approvedLeaves: LeaveRequest[];
  declinedLeaves: LeaveRequest[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchLeaveTypes: () => Promise<LeaveType[]>;
  fetchLeaveBalance: (userId: string) => Promise<LeaveBalance[]>;
  fetchPendingLeaves: (userId: string) => Promise<LeaveRequest[]>;
  fetchApprovedLeaves: (userId: string) => Promise<LeaveRequest[]>;
  fetchDeclinedLeaves: (userId: string) => Promise<LeaveRequest[]>;
  submitLeaveRequest: (data: {
    leave_type_id: number | string;
    title: string;
    start_date: string;
    end_date: string;
    body: string;
  }) => Promise<any>;
  updateLeaveRequest: (leaveId: number, data: {
    leave_type_id: number | string;
    title: string;
    start_date: string;
    end_date: string;
    body: string;
    attachment?: string | null;
  }, userId: string) => Promise<any>;
  withdrawLeave: (leaveId: number, userId: string) => Promise<any>;
  deletePendingLeave: (leaveId: number, userId: string) => Promise<any>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetStore: () => void;
}

const useEmployeeLeaveStore = create<EmployeeLeaveStore>((set, get) => ({
  // Initial State
  leaveTypes: [],
  leaveBalance: [],
  pendingLeaves: [],
  approvedLeaves: [],
  declinedLeaves: [],
  loading: false,
  error: null,

  /* ------------------------------------------------------------------ */
  /*  Fetch Leave Types                                                  */
  /* ------------------------------------------------------------------ */
  fetchLeaveTypes: async () => {
    console.log('Store: fetchLeaveTypes called');
    set({ loading: true, error: null });
    try {
      const response = await getLeaveTypes();
      console.log('Store: getLeaveTypes response:', response);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch leave types');
      }
      console.log('Store: Setting leaveTypes:', response.data);
      set({ leaveTypes: response.data || [], loading: false });
      return response.data || [];
    } catch (e: any) {
      console.error('Store: fetchLeaveTypes error:', e);
      const msg = e.response?.data?.error?.message || e.message || 'Failed to fetch leave types';
      set({ error: msg, loading: false, leaveTypes: [] });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Fetch Leave Balance                                                */
  /* ------------------------------------------------------------------ */
  fetchLeaveBalance: async (userId: string) => {
    console.log('Store: fetchLeaveBalance called with userId:', userId);
    set({ loading: true, error: null });
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await getLeaveBalance(userId);
      console.log('Store: getLeaveBalance response:', response);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch leave balance');
      }
      console.log('Store: Setting leaveBalance:', response.data);
      set({ leaveBalance: response.data || [], loading: false });
      return response.data || [];
    } catch (e: any) {
      console.error('Store: fetchLeaveBalance error:', e);
      const msg = e.response?.data?.error?.message || e.message || 'Failed to fetch leave balance';
      set({ error: msg, loading: false, leaveBalance: [] });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Fetch Pending Leaves                                               */
  /* ------------------------------------------------------------------ */
  fetchPendingLeaves: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await getPendingLeaves(userId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch pending leaves');
      }
      set({ pendingLeaves: response.data || [], loading: false });
      return response.data || [];
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to fetch pending leaves';
      set({ error: msg, loading: false, pendingLeaves: [] });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Fetch Approved Leaves                                              */
  /* ------------------------------------------------------------------ */
  fetchApprovedLeaves: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await getApprovedLeaves(userId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch approved leaves');
      }
      set({ approvedLeaves: response.data || [], loading: false });
      return response.data || [];
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to fetch approved leaves';
      set({ error: msg, loading: false, approvedLeaves: [] });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Fetch Declined Leaves                                              */
  /* ------------------------------------------------------------------ */
  fetchDeclinedLeaves: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      if (!userId) throw new Error('User ID is required');
      const response = await getDeclinedLeaves(userId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch declined leaves');
      }
      set({ declinedLeaves: response.data || [], loading: false });
      return response.data || [];
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to fetch declined leaves';
      set({ error: msg, loading: false, declinedLeaves: [] });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Submit Leave Request                                                */
  /* ------------------------------------------------------------------ */
  submitLeaveRequest: async (leaveData) => {
    set({ loading: true, error: null });
    try {
      // Convert leave_type_id to number if it's a string
      const requestData = {
        ...leaveData,
        leave_type_id: typeof leaveData.leave_type_id === 'string'
          ? parseInt(leaveData.leave_type_id)
          : leaveData.leave_type_id
      };

      const response = await createLeaveRequest(requestData);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to submit leave request');
      }
      set({ loading: false });
      return response.data;
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to submit leave request';
      set({ error: msg, loading: false });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Update Leave Request                                               */
  /* ------------------------------------------------------------------ */
  updateLeaveRequest: async (leaveId: number, leaveData: any, userId: string) => {
    set({ loading: true, error: null });
    try {
      // Convert leave_type_id to number if it's a string
      const requestData = {
        ...leaveData,
        leave_type_id: typeof leaveData.leave_type_id === 'string'
          ? parseInt(leaveData.leave_type_id)
          : leaveData.leave_type_id
      };

      const response = await updateLeaveRequestService(leaveId, requestData);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update leave request');
      }
      set({ loading: false });
      // Refresh leaves and balance
      if (userId) {
        get().fetchPendingLeaves(userId);
        get().fetchApprovedLeaves(userId);
        get().fetchLeaveBalance(userId);
      }
      return response.data;
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to update leave request';
      set({ error: msg, loading: false });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Withdraw Approved Leave                                            */
  /* ------------------------------------------------------------------ */
  withdrawLeave: async (leaveId: number, userId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await withdrawLeaveService(leaveId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to withdraw leave');
      }
      set({ loading: false });
      // Refresh approved leaves and balance
      if (userId) {
        get().fetchApprovedLeaves(userId);
        get().fetchLeaveBalance(userId);
      }
      return response.data;
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to withdraw leave';
      set({ error: msg, loading: false });
      throw e;
    }
  },

  /* ------------------------------------------------------------------ */
  /*  Delete Pending Leave                                               */
  /* ------------------------------------------------------------------ */
  deletePendingLeave: async (leaveId: number, userId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await deletePendingLeaveService(leaveId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete leave request');
      }
      set({ loading: false });
      // Refresh pending leaves
      if (userId) {
        get().fetchPendingLeaves(userId);
      }
      return response.data;
    } catch (e: any) {
      const msg = e.response?.data?.error?.message || e.message || 'Failed to delete leave request';
      set({ error: msg, loading: false });
      throw e;
    }
  },

  // Helper actions
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),

  // Reset store to initial state
  resetStore: () => set({
    leaveTypes: [],
    leaveBalance: [],
    pendingLeaves: [],
    approvedLeaves: [],
    declinedLeaves: [],
    loading: false,
    error: null
  }),
}));

// Register this store for centralized reset
registerStoreReset('employeeLeave', () => useEmployeeLeaveStore.getState().resetStore());

export default useEmployeeLeaveStore;
